<template>
  <RadioFilter
    :items="radioItems"
    @changed="onChange"
  />
</template>

<script setup lang="ts">
import { computed, inject, onMounted, onUnmounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { RadioFilter, type Filter, type Item } from '@bc/discovery/ui/filters';
import type { InstantSearch } from 'instantsearch.js';
import { getFacetCounts } from '../../utils/options';

defineOptions({ name: 'CustomAisSingleSelectFilter' });

const props = defineProps<{ filter: Filter }>();

const { t } = useI18n();

const ALL_VALUE = '';

// Vue InstantSearch provides the InstantSearch instance through Vue's provide/inject under this key.
// We use the underlying Algolia helper to read and write disjunctive facet refinements imperatively,
// which gives us proper single-select semantics on top of a disjunctive facet (the alcohol filter
// is single-select but the underlying facet type is disjunctive). Reading from the helper avoids
// the AisRefinementList "items only include aggregated values" limitation that breaks for our
// virtual-field-backed facet (alcohol_content has no aggregable values in the index).
const instantSearchInstance = inject<InstantSearch | undefined>('$_ais_instantSearchInstance', undefined);

const helper = computed(() => instantSearchInstance?.helper);

// Track the active refinement reactively. We snapshot it on every render trigger so the radio's
// "isRefined" state stays in sync with AIS state.
const renderTrigger = ref(0);

// A URL built before this facet became single-select (or just hand-edited/shared) can carry
// several comma-separated values for it - the generic hash-param parsing in
// history-router-product.ts has no notion of "this facet only allows one value" and splits on
// every comma regardless. Left alone, the search still ORs every refinement together (the
// loosest cumulative threshold wins), but this component would only ever display
// `refinements[0]` - possibly a *stricter* tier than the one actually applied. Normalizing to
// just the loosest keeps the radio, the URL, and the actual results in agreement.
const normalizeToSingleRefinement = () => {
  if (!helper.value) {
    return;
  }
  const attribute = props.filter.name;
  const current = helper.value.state.disjunctiveFacetsRefinements?.[attribute] ?? [];
  if (current.length <= 1) {
    return;
  }

  const choiceOrder = (props.filter.choices ?? []).map((choice) => choice.slug);
  const [loosest] = [...current].sort((a, b) => choiceOrder.indexOf(a) - choiceOrder.indexOf(b));

  current.filter((value) => value !== loosest).forEach((value) => helper.value.removeDisjunctiveFacetRefinement(attribute, value));
  helper.value.search();
  // The refinement mutation above happens on the AIS helper's plain (non-reactive) state object,
  // so nothing tells refinedValue/radioItems to recompute on its own - bump the same trigger
  // their computeds already depend on to force the display to catch up with the corrected state.
  renderTrigger.value++;
};

const onRender = () => {
  renderTrigger.value++;
  normalizeToSingleRefinement();
};

onMounted(() => {
  instantSearchInstance?.on('render', onRender);
  normalizeToSingleRefinement();
});

onUnmounted(() => {
  instantSearchInstance?.removeListener('render', onRender);
});

const refinedValue = computed<string>(() => {
  // Touch the trigger so the computed re-evaluates on every search render.
  void renderTrigger.value;
  if (!helper.value) {
    return ALL_VALUE;
  }
  const refinements = helper.value.state.disjunctiveFacetsRefinements?.[props.filter.name] ?? [];
  return refinements[0] ?? ALL_VALUE;
});

// Unlike alcohol_content (a virtual field with no aggregable values at all - see the comment
// above), max_discount_rate.range IS a real aggregated facet, so it does have per-choice counts
// available from the last search response. There's no single meaningful count for the "all"
// choice itself (it's a clear action, not a bucket), so it's left uncounted.
//
// undefined (as opposed to {}) specifically means "no real count data for this facet" - covers
// both "no search response yet" AND "this facet has no aggregation entry at all" (a virtual
// field like alcohol_content). getFacetCounts() alone can't distinguish that second case from
// "aggregation entry exists but every bucket is 0" - both return {} - so it's checked explicitly
// here: without it, alcohol_content's choices (never aggregated) would read as "always 0" and
// get hidden by radioItems' filter below the moment any search response arrives.
const facetCounts = computed<Record<string, number> | undefined>(() => {
  void renderTrigger.value;
  const results = instantSearchInstance?.helper?.lastResults;
  if (!results) {
    return undefined;
  }
  const hasAggregation =
    results.facets?.some(({ name }) => name === props.filter.name) ||
    results.disjunctiveFacets?.some(({ name }) => name === props.filter.name);
  if (!hasAggregation) {
    return undefined;
  }
  return getFacetCounts(props.filter.name, results);
});

const radioItems = computed<Item[]>(() => {
  const choices = props.filter.choices ?? [];
  const active = refinedValue.value;
  const counts = facetCounts.value;
  const choiceItems = choices
    .map((choice) => ({
      label: choice.name,
      value: choice.slug,
      isRefined: active === choice.slug,
      count: counts?.[choice.slug],
      data: undefined,
    }))
    // Mirrors AisRefinementList's own behavior (checkbox facets like Margin): a choice with no
    // matching products is hidden. The currently refined choice stays visible even at 0 - same
    // reason AisRefinementList keeps it too - otherwise there'd be no way to deselect it. Skipped
    // entirely while counts are unknown (!counts), so choices don't flash empty before the first
    // search response arrives.
    .filter((item) => !counts || item.isRefined || (item.count ?? 0) > 0);

  return [
    {
      label: t('All products'),
      value: ALL_VALUE,
      isRefined: active === ALL_VALUE,
      count: undefined,
      data: undefined,
    },
    ...choiceItems,
  ];
});

const onChange = (item: Item): void => {
  if (!helper.value) {
    return;
  }
  const attribute = props.filter.name;
  const current = helper.value.state.disjunctiveFacetsRefinements?.[attribute] ?? [];

  // Single-select: clear any existing refinement for this attribute first.
  current.forEach((value) => helper.value.removeDisjunctiveFacetRefinement(attribute, value));

  if (item.value !== ALL_VALUE) {
    helper.value.addDisjunctiveFacetRefinement(attribute, item.value);
  }

  helper.value.search();
};
</script>
